import {
  poseKey,
  SEMAPHORE_CODE,
  SEMAPHORE_DECODE,
  type FlagPose,
} from "./code";

export type UnknownPolicy = "skip" | "throw" | "placeholder";

export interface EncodedSemaphore {
  /// The original character, upper-cased. `null` for word breaks.
  letter: string | null;
  /// The arm pose for this letter. `null` for word breaks.
  pose: FlagPose | null;
  /// True when this slot is a word break (rendered as a "rest" pose
  /// in the UI, both arms straight down).
  isWordBreak: boolean;
}

export interface EncodeOptions {
  /// What to do with characters not in the semaphore alphabet
  /// (digits, punctuation, accented letters, etc.).
  ///   "skip"        — drop silently
  ///   "throw"       — throw with the offending character
  ///   "placeholder" — emit an "unknown" slot with letter='?' (default)
  onUnknown?: UnknownPolicy;
}

/// Encode a text string into a sequence of semaphore poses. Spaces
/// produce word-break slots so a renderer can show a pause (rest
/// position) between words.
export function encode(
  text: string,
  options: EncodeOptions = {},
): EncodedSemaphore[] {
  const onUnknown = options.onUnknown ?? "placeholder";
  const out: EncodedSemaphore[] = [];
  // Split into words on whitespace; insert one word-break between
  // each pair of words.
  const words = text.split(/\s+/).filter((w) => w.length > 0);
  words.forEach((word, idx) => {
    if (idx > 0) {
      out.push({ letter: null, pose: null, isWordBreak: true });
    }
    for (const char of word) {
      const upper = char.toUpperCase();
      const pose = SEMAPHORE_CODE[upper];
      if (pose) {
        out.push({ letter: upper, pose, isWordBreak: false });
      } else if (onUnknown === "throw") {
        throw new Error(`semaphore_encode_unknown_char_${JSON.stringify(char)}`);
      } else if (onUnknown === "placeholder") {
        out.push({ letter: "?", pose: null, isWordBreak: false });
      }
      // skip: just drop
    }
  });
  return out;
}

export interface DecodeOptions {
  onUnknown?: UnknownPolicy;
}

/// Decode a sequence of poses back to text. `null` poses are treated
/// as word breaks. Inverse of `encode`.
export function decode(
  poses: readonly (FlagPose | null)[],
  options: DecodeOptions = {},
): string {
  const onUnknown = options.onUnknown ?? "placeholder";
  const out: string[] = [];
  let currentWord = "";
  for (const pose of poses) {
    if (pose === null) {
      if (currentWord.length > 0) {
        out.push(currentWord);
        currentWord = "";
      }
      continue;
    }
    const letter = SEMAPHORE_DECODE[poseKey(pose)];
    if (letter !== undefined) {
      currentWord += letter;
    } else if (onUnknown === "throw") {
      throw new Error(
        `semaphore_decode_unknown_pose_${pose.right}_${pose.left}`,
      );
    } else if (onUnknown === "placeholder") {
      currentWord += "?";
    }
  }
  if (currentWord.length > 0) out.push(currentWord);
  return out.join(" ");
}
